import { useCallback, useEffect, useMemo, useState } from "react";
import { BuildingOfficeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  createGpRechnungsadresse,
  deleteGpRechnungsadresse,
  formatGpRechnungsadresse,
  listGpRechnungsadressen,
  updateGpRechnungsadresse,
  type GpRechnungsadresse,
} from "@/lib/kontakte";
import { useRealtimeSync } from "@/lib/useRealtime";
import { ROW_ACTIONS_HEADER_CLASS, TableRowActions } from "@/pages/kontakte/TableRowActions";

interface AdresseForm {
  bezeichnung: string;
  firma: string;
  adresszusatz: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
  isDefault: boolean;
}

const EMPTY_FORM: AdresseForm = {
  bezeichnung: "",
  firma: "",
  adresszusatz: "",
  strasse: "",
  hausnummer: "",
  plz: "",
  ort: "",
  land: "Deutschland",
  isDefault: false,
};

function toForm(addr: GpRechnungsadresse): AdresseForm {
  return {
    bezeichnung: addr.bezeichnung || "",
    firma: addr.firma || "",
    adresszusatz: addr.adresszusatz || "",
    strasse: addr.strasse || "",
    hausnummer: addr.hausnummer || "",
    plz: addr.plz || "",
    ort: addr.ort || "",
    land: addr.land || "Deutschland",
    isDefault: !!addr.is_default,
  };
}

function formToPayload(form: AdresseForm) {
  return {
    bezeichnung: form.bezeichnung.trim() || null,
    firma: form.firma.trim() || null,
    adresszusatz: form.adresszusatz.trim() || null,
    strasse: form.strasse.trim() || null,
    hausnummer: form.hausnummer.trim() || null,
    plz: form.plz.trim() || null,
    ort: form.ort.trim() || null,
    land: form.land.trim() || "Deutschland",
    is_default: form.isDefault,
  };
}

interface KundeRechnungsadressenSectionProps {
  geschaeftspartnerId: number;
}

export function KundeRechnungsadressenSection({
  geschaeftspartnerId,
}: KundeRechnungsadressenSectionProps) {
  const [rows, setRows] = useState<GpRechnungsadresse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<AdresseForm>(EMPTY_FORM);
  const [formBaseline, setFormBaseline] = useState<AdresseForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!options?.silent) setLoading(true);
      setError(null);
      try {
        const list = await listGpRechnungsadressen(geschaeftspartnerId);
        setRows(list);
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : "Rechnungsadressen konnten nicht geladen werden.",
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [geschaeftspartnerId],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["kontakte"],
    paused: formOpen,
    enabled: Number.isFinite(geschaeftspartnerId),
    onChange: () => void reload({ silent: true }),
  });

  function openCreate() {
    const initial = { ...EMPTY_FORM, isDefault: rows.length === 0 };
    setEditId(null);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(addr: GpRechnungsadresse) {
    const initial = toForm(addr);
    setEditId(addr.id);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setFormOpen(true);
  }

  async function handleSave() {
    const payload = formToPayload(form);
    if (
      !payload.bezeichnung &&
      !payload.firma &&
      !payload.strasse &&
      !payload.plz &&
      !payload.ort
    ) {
      setFormError("Bitte mindestens Bezeichnung, Firma oder Adresse angeben.");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      if (editId) {
        await updateGpRechnungsadresse(geschaeftspartnerId, editId, payload);
      } else {
        await createGpRechnungsadresse(geschaeftspartnerId, payload);
      }
      setFormOpen(false);
      await reload({ silent: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Adresse konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(addr: GpRechnungsadresse) {
    if (!window.confirm(`Rechnungsadresse „${formatGpRechnungsadresse(addr)}" wirklich löschen?`)) {
      return;
    }
    try {
      await deleteGpRechnungsadresse(geschaeftspartnerId, addr.id);
      await reload({ silent: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Adresse konnte nicht gelöscht werden.",
      );
    }
  }

  const columns = useMemo(
    () => [
      {
        key: "adresse",
        header: "Adresse",
        sortable: true,
        sortValue: (addr: GpRechnungsadresse) => formatGpRechnungsadresse(addr),
        render: (addr: GpRechnungsadresse) => (
          <div className="space-y-0.5">
            <div className="font-medium text-label">{formatGpRechnungsadresse(addr)}</div>
            <div className="flex flex-wrap gap-1.5">
              {addr.is_default ? (
                <span className="inline-flex rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">
                  Standard
                </span>
              ) : null}
              {addr.hero_contact_id != null ? (
                <span className="inline-flex rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                  aus Hero
                </span>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "actions",
        header: "",
        headerClassName: ROW_ACTIONS_HEADER_CLASS.two,
        render: (addr: GpRechnungsadresse) => (
          <TableRowActions onEdit={() => openEdit(addr)} onDelete={() => void handleDelete(addr)} />
        ),
      },
    ],
    [],
  );

  return (
    <Card
      title="Rechnungsadressen"
      description="Abweichende Anschriften für Rechnungen. Die Standardadresse wird beim Erstellen einer Rechnung automatisch verwendet."
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={openCreate}
          leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}
        >
          Adresse hinzufügen
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" title="Fehler" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BuildingOfficeIcon />}
          title="Keine abweichende Rechnungsadresse"
          description="Rechnungen gehen an die normale Kundenadresse. Lege eine Adresse an, wenn z. B. eine zentrale Buchhaltung die Rechnungen erhält."
          action={
            <Button size="sm" onClick={openCreate} leftIcon={<PlusIcon className="h-4 w-4" aria-hidden />}>
              Adresse hinzufügen
            </Button>
          }
        />
      ) : (
        <Table
          columns={columns}
          rows={rows}
          rowKey={(addr) => addr.id}
          rowClassName={() => "align-middle"}
          stickyEndColumnCount={1}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editId ? "Rechnungsadresse bearbeiten" : "Rechnungsadresse hinzufügen"}
        dirty={hasFormChanges(form, formBaseline)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </>
        }
      >
        {formError ? (
          <Alert variant="error" title="Eingabe" className="mb-4">
            {formError}
          </Alert>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Bezeichnung"
            value={form.bezeichnung}
            onChange={(e) => setForm((f) => ({ ...f, bezeichnung: e.target.value }))}
            placeholder="z. B. Zentrale, Buchhaltung"
          />
          <TextInput
            label="Firma (optional)"
            value={form.firma}
            onChange={(e) => setForm((f) => ({ ...f, firma: e.target.value }))}
          />
          <TextInput
            label="Adresszusatz"
            className="sm:col-span-2"
            value={form.adresszusatz}
            onChange={(e) => setForm((f) => ({ ...f, adresszusatz: e.target.value }))}
            placeholder="z. B. H12 Accounts Payable"
          />
          <TextInput
            label="Straße"
            value={form.strasse}
            onChange={(e) => setForm((f) => ({ ...f, strasse: e.target.value }))}
          />
          <TextInput
            label="Hausnummer"
            value={form.hausnummer}
            onChange={(e) => setForm((f) => ({ ...f, hausnummer: e.target.value }))}
          />
          <TextInput
            label="PLZ"
            value={form.plz}
            onChange={(e) => setForm((f) => ({ ...f, plz: e.target.value }))}
          />
          <TextInput
            label="Ort"
            value={form.ort}
            onChange={(e) => setForm((f) => ({ ...f, ort: e.target.value }))}
          />
          <TextInput
            label="Land"
            value={form.land}
            onChange={(e) => setForm((f) => ({ ...f, land: e.target.value }))}
          />
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-label">
            <input
              type="checkbox"
              className="rounded border-separator"
              checked={form.isDefault}
              onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
            />
            Als Standard-Rechnungsadresse
          </label>
        </div>
      </Dialog>
    </Card>
  );
}
